/** Data-only native call waveform. The source clock survives cuts and trims. */
export interface NativeCallLaneBar {
  version: 1;
  index: number;
  count: number;
  sourceStartFrame: number;
  speakingWindows: Array<{ startFrame: number; endFrame: number }>;
  idleColor: string;
  barColor: string;
}

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const frame = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= 10_000_000;
const color = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);

export function isNativeCallLaneBar(value: unknown): value is NativeCallLaneBar {
  if (!object(value) || value.version !== 1 || !frame(value.index) || !frame(value.count)
    || value.count < 1 || value.count > 64 || value.index >= value.count
    || !frame(value.sourceStartFrame) || !color(value.idleColor) || !color(value.barColor)
    || Object.keys(value).some(key=>!['version','index','count','sourceStartFrame','speakingWindows','idleColor','barColor'].includes(key))
    || !Array.isArray(value.speakingWindows) || value.speakingWindows.length > 1024) return false;
  let previousEnd = 0;
  for (const window of value.speakingWindows) {
    if (!object(window) || !frame(window.startFrame) || !frame(window.endFrame)
      || Object.keys(window).some(key=>key!=='startFrame'&&key!=='endFrame')
      || window.endFrame <= window.startFrame || window.startFrame < previousEnd) return false;
    previousEnd = window.endFrame;
  }
  return true;
}

export function hasNativeCallLaneBars(document: unknown): boolean {
  if (!object(document) || !object(document.items)) return false;
  return Object.values(document.items).some(item => object(item) && item.wakaCallLaneBar !== undefined);
}

export function validateNativeCallLaneBars(document: unknown): boolean {
  if (!object(document) || !object(document.items)) return true;
  return Object.values(document.items).every(item => !object(item) || item.wakaCallLaneBar === undefined
    || (item.type === 'solid' && isNativeCallLaneBar(item.wakaCallLaneBar)
      && typeof item.width === 'number' && Number.isFinite(item.width) && item.width > 0
      && typeof item.height === 'number' && Number.isFinite(item.height) && item.height > 0));
}

/** Exactly the original StoryTemplate callLaneBarLevels formula, shared by both renderers. */
export function callLaneBarLevel(frameNumber: number, index: number, count: number): number {
  const center = count > 1 ? 1 - Math.abs(index / (count - 1) - 0.5) * 1.2 : 1;
  const wobble = 0.5 * Math.abs(Math.sin(frameNumber * 0.37 + index * 1.71))
    + 0.5 * Math.abs(Math.sin(frameNumber * 0.23 + index * 0.93 + 1.3));
  return Math.max(0.12, Math.min(1, center * (0.25 + 0.75 * wobble)));
}

export function nativeCallLaneBarAt(value: NativeCallLaneBar, localFrame: number, peakHeight: number, width: number, speakingColor=value.barColor) {
  const sourceFrame = value.sourceStartFrame + localFrame;
  const speaking = value.speakingWindows.some(window => sourceFrame >= window.startFrame && sourceFrame < window.endFrame);
  return {
    speaking,
    height: speaking ? Math.max(width * 2 / 3, callLaneBarLevel(sourceFrame, value.index, value.count) * peakHeight) : width,
    color: speaking ? speakingColor : value.idleColor,
  };
}

export function advanceNativeCallLaneBar<T>(item: T, frames: number): T {
  if (!item || typeof item !== 'object' || !('wakaCallLaneBar' in item)
    || !isNativeCallLaneBar(item.wakaCallLaneBar)) return item;
  const sourceStartFrame=item.wakaCallLaneBar.sourceStartFrame+frames;
  if(!Number.isSafeInteger(sourceStartFrame)||sourceStartFrame<0||sourceStartFrame>10_000_000)return item;
  return {...item, wakaCallLaneBar: {...item.wakaCallLaneBar,
    sourceStartFrame}};
}
